import React, {useState, useEffect} from 'react';
import TabContentWrapper from '../TabContentWrapper';
import {SlideToggle, ControlButton, Glyphs, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import DataTable from '../DataTable';
import {IConversationData} from '../../../model/conversationModel';
import TabHeader from '../TabHeader';
import {IWorkerState} from '../../../model/workerState';
import {conversationsStore, updateConversationsFromServer, updateConversationsState} from '../../../context/conversationsProvider';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import styled from 'styled-components';
import Constants from '../../../constants/Constants';
import {useQueryParam, StringParam} from 'use-query-params';
import {mediaStore} from '../../../context/mediaProvider';
import config from '../../../service/config';
import conversationApi from '../../../service/conversationApi';
import {updateSelectedConversationState} from '../../../context/selectedConversationProvider';
import ClearConversationModal from '../../modals/ClearConversationModal';

const DataTableWrapper = styled.div`
   .data-table-row, .data-table-header {
      grid-template-columns: repeat(2, 0.75fr) 1.25fr 10rem;
      grid-gap: 10px;
   }

   ul {
      grid-template-columns: repeat( auto-fit, minmax(19rem, 1fr) );
   }
`;

const ConversationSettingsHeaderWrapper = styled.div`
   .conversation-toggle {
      padding: 4px;
   }
`;

const DownloadButton = styled(ControlButton)`
   min-width: max-content;
`;

export const ConversationSettingsBase: React.FC<IWorkerState & IStateMappedProps> = ({conversations, isWorking: initialIsWorking, hasError: initialHasError, Hash, Title}) => {
   const {t} = useTranslation();
   const [showDeleteConversationModal, setShowDeleteConversationModal] = useState(false);
   const [conversationParam] = useQueryParam(Constants.queryParams.conversation, StringParam);
   const [loadingState, setLoadingState] = useState({isWorking: initialIsWorking, hasError: initialHasError});

   const getConversations = async () => {
      try {
         conversations || setLoadingState({isWorking: true, hasError: false});
         await updateConversationsFromServer(Hash);
         setLoadingState({isWorking: false, hasError: false});
      } catch {
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   useEffect(() => {
      void getConversations();
   }, []);

   useEffect(() => {
      void (!conversations?.findIndex(conversation => conversation.VideoReviewId === conversationParam) && getConversations());
   }, [conversationParam]);

   const conversationColumnLabels = [
      t('conversations.columnLabels.comments'),
      t('conversations.columnLabels.participants'),
      t('conversations.columnLabels.created'),
      t('conversations.columnLabels.actions')
   ];

   const createConversation = async () => {
      const newConversation = await conversationApi.createConversation(Hash, Title);
      conversations.push(newConversation);
      updateConversationsState({conversations: conversations});
      updateSelectedConversationState({Title: newConversation.Title, VideoReviewId: newConversation.VideoReviewId, ConversationHash: newConversation.ConversationHash, SelectedComment: null});
   };

   const onToggleConversationClick = async () => {
      try {
         if (conversations?.length === 0) {
            setLoadingState({isWorking: true, hasError: false});
            await createConversation();
            setLoadingState({isWorking: false, hasError: false});
         } else {
            setShowDeleteConversationModal(true);
         }
      } catch {
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   const activeConversation = conversations?.length ? conversations[0] : null;

   const onDownloadCommentsClick = async () => {
      if (!activeConversation) {
         return;
      }
      try {
         await conversationApi.downloadSingleConversationData(Hash, activeConversation.ConversationHash);
      } catch {
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   return (
      <TabContentWrapper
         showSpinner={loadingState.isWorking}
         showError={loadingState.hasError}
      >
         <ConversationSettingsHeaderWrapper>
            <TabHeader
               leftLabel={config.featureSwitches.UseSingleConversation ? t('conversations.ConversationLabel') : t('conversations.tabName')}
               rightLabel=""
               hideRightLabelInMobileView={true}
               button={(
                  <SlideToggle
                     className="conversation-toggle"
                     isChecked={conversations?.length > 0}
                     onClick={onToggleConversationClick}
                  />
               )}
            />
         </ConversationSettingsHeaderWrapper>
         <DataTableWrapper data-test-id="conversations-list">
            <DataTable
               columnLabels={conversationColumnLabels}
               emptyStateLabel={t('conversations.singleConversationEmptyStateString')}
               tableRows={activeConversation ? [{
                  tableRow: [
                     activeConversation.CommentCount,
                     activeConversation.CommenterCount,
                     dayjs(activeConversation.DateCreated).format('MMMM D, YYYY'),
                     <DownloadButton
                        onClick={onDownloadCommentsClick}
                        buttonType={TscButtonType.tertiary}
                        glyph={<Glyphs.Download16x16 />}
                        label={t('resetData.downloadData')}
                        title={t('conversations.actions.download')}
                        testId="download-conversation-button"
                        key="download-conversation-button"
                     />
                  ]
               }] : []}
            />
         </DataTableWrapper>
         {showDeleteConversationModal && (
            <ClearConversationModal
               conversationHash={activeConversation?.ConversationHash}
               isDeleting={true}
               onClose={() => setShowDeleteConversationModal(false)}
            />
         )}
      </TabContentWrapper>
   );
};

export interface IStateMappedProps {
   conversations: IConversationData[];
   Hash: string;
   Title: string;
}

const mapStatesToProps = (
   {isWorking, hasError}: IWorkerState,
   {conversations}: Partial<IStateMappedProps>,
   {Hash, Title}: Partial<IStateMappedProps>
): IWorkerState & IStateMappedProps => ({conversations, isWorking, hasError, Hash, Title});

export default withMemoizedContexts(mapStatesToProps, conversationsStore, mediaStore)(ConversationSettingsBase);
